import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "../lib/api";
import {
  Building2, Home, DoorClosed, TrendingUp, AlertTriangle, Wallet, Activity,
  Wrench, FileWarning, ShieldAlert, Landmark, CalendarClock, ArrowUpRight, Layers, PiggyBank, AlertCircle,
  Banknote, BellRing, ClipboardCheck, MessageSquare,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart,
} from "recharts";
import { DocLinks } from "../components/DocActions";

const CHART = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#7c3aed", "#06b6d4"];

const sevDot = { red: "bg-red-500", orange: "bg-amber-500", blue: "bg-blue-500", green: "bg-emerald-500" };
const sevText = { red: "text-red-600", orange: "text-amber-600", blue: "text-blue-600", green: "text-emerald-600" };

function Kpi({ icon: Icon, label, value, sub, tone, accent, i, to }) {
  const navigate = useNavigate();
  const clickable = !!to;
  return (
    <div
      onClick={clickable ? () => navigate(to) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter") navigate(to); } : undefined}
      className={`rounded-2xl border border-border bg-card p-5 kpi-enter transition-all ${clickable ? "cursor-pointer hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5" : "hover:shadow-md"}`}
      style={{ animationDelay: `${i * 40}ms` }} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent || "bg-blue-50 text-blue-600 dark:bg-blue-500/10"}`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        {clickable && <ArrowUpRight className="w-4 h-4 text-muted-foreground" />}
      </div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className={`font-heading text-2xl font-bold tracking-tight mt-1 ${tone || ""}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

const card = "rounded-2xl border border-border bg-card p-5";

function MiniStat({ label, value, tone, wide, to }) {
  const navigate = useNavigate();
  const clickable = !!to;
  return (
    <div
      onClick={clickable ? () => navigate(to) : undefined}
      role={clickable ? "button" : undefined}
      className={`rounded-xl bg-muted/40 p-3 transition-colors ${wide ? "col-span-2" : ""} ${clickable ? "cursor-pointer hover:bg-muted/70" : ""}`}
      data-testid={`ministat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className={`font-heading text-lg font-bold tracking-tight mt-0.5 ${tone || ""}`}>{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [z, setZ] = useState(null);
  const [renewals, setRenewals] = useState(0);
  const [cl, setCl] = useState(null);
  const [comp, setComp] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setS(r.data)).catch(() => {});
    api.get("/zelle/stats").then((r) => setZ(r.data)).catch(() => {});
    api.get("/lease-renewals").then((r) => setRenewals(r.data.filter((t) => t.status === "Open").length)).catch(() => {});
    api.get("/checklists/dashboard/alerts").then((r) => setCl(r.data)).catch(() => {});
    api.get("/compliance/dashboard/stats").then((r) => setComp(r.data)).catch(() => {});
    api.get("/messaging/dashboard/stats").then((r) => setMsg(r.data)).catch(() => {});
  }, []);
  if (!s) return <div className="p-8 text-muted-foreground">Loading dashboard…</div>;

  const L = {
    overdueRent: `/payments?overdue=1&label=${encodeURIComponent("Overdue Rent")}`,
    insurance: `/insurance?dueField=renewal_date&dueWithin=90&label=${encodeURIComponent("Insurance Renewals Due")}`,
    taxes: `/taxes?dueField=due_date&dueWithin=120&not_status=Paid&label=${encodeURIComponent("Property Taxes Due")}`,
    leaseExp: `/leases?expiring=90&label=${encodeURIComponent("Leases Expiring (90 days)")}`,
    mortgage: `/mortgages?dueField=due_date&dueWithin=30&label=${encodeURIComponent("Mortgage Payments Due")}`,
    openMaint: `/maintenance?not_status=${encodeURIComponent("Completed,Closed,Cancelled")}&label=${encodeURIComponent("Open Maintenance")}`,
    partial: `/payments?status=Partial&label=${encodeURIComponent("Partial Payments")}`,
  };
  const actionLink = (type) => ({
    "Late Rent": L.overdueRent, "Insurance Due": L.insurance, "Property Tax Due": L.taxes,
    "Lease Expiring": L.leaseExp, "Mortgage Due": L.mortgage, "Maintenance Open": L.openMaint,
  }[type] || null);

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Kora Holding, LLC</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Executive Dashboard</h1>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Portfolio Value</p>
          <p className="font-heading text-2xl font-bold text-primary">{money(s.portfolio_value)}</p>
        </div>
      </div>

      {/* Needs Attention strip */}
      {(() => {
        const items = [
          { show: (s.delinquent_rent || 0) > 0, value: money(s.delinquent_rent), label: "Overdue Rent", to: L.overdueRent, icon: AlertTriangle, tone: "text-red-600", dot: "bg-red-500", accent: "hover:border-red-300" },
          { show: (s.upcoming_lease_expirations || 0) > 0, value: s.upcoming_lease_expirations, label: "Leases Expiring (90d)", to: L.leaseExp, icon: FileWarning, tone: "text-amber-600", dot: "bg-amber-500", accent: "hover:border-amber-300" },
          { show: (s.upcoming_insurance_renewals || 0) > 0, value: s.upcoming_insurance_renewals, label: "Renewals Due (90d)", to: L.insurance, icon: ShieldAlert, tone: "text-blue-600", dot: "bg-blue-500", accent: "hover:border-blue-300" },
          { show: renewals > 0, value: renewals, label: "Renewals to Action", to: "/renewals", icon: CalendarClock, tone: "text-violet-600", dot: "bg-violet-500", accent: "hover:border-violet-300" },
          { show: (s.open_maintenance || 0) > 0, value: s.open_maintenance, label: "Open Maintenance", to: L.openMaint, icon: Wrench, tone: "text-amber-600", dot: "bg-amber-500", accent: "hover:border-amber-300" },
          { show: (cl?.move_ins_soon || 0) > 0, value: cl?.move_ins_soon, label: "Move-Ins Soon", to: "/checklists", icon: ClipboardCheck, tone: "text-emerald-600", dot: "bg-emerald-500", accent: "hover:border-emerald-300" },
          { show: (cl?.move_outs_soon || 0) > 0, value: cl?.move_outs_soon, label: "Move-Outs Soon", to: "/checklists", icon: ClipboardCheck, tone: "text-amber-600", dot: "bg-amber-500", accent: "hover:border-amber-300" },
          { show: (cl?.overdue_tasks || 0) > 0, value: cl?.overdue_tasks, label: "Checklist Tasks Overdue", to: "/checklists", icon: ClipboardCheck, tone: "text-red-600", dot: "bg-red-500", accent: "hover:border-red-300" },
          { show: (comp?.overdue || 0) > 0, value: comp?.overdue, label: "Compliance Overdue", to: "/compliance?status=Overdue", icon: ShieldAlert, tone: "text-red-600", dot: "bg-red-500", accent: "hover:border-red-300" },
          { show: (comp?.due_soon || 0) > 0, value: comp?.due_soon, label: "Compliance Due Soon", to: "/compliance?status=Due+Soon", icon: ShieldAlert, tone: "text-amber-600", dot: "bg-amber-500", accent: "hover:border-amber-300" },
          { show: (comp?.needs_review || 0) > 0, value: comp?.needs_review, label: "Compliance Needs Review", to: "/compliance?status=Needs+Review", icon: ShieldAlert, tone: "text-violet-600", dot: "bg-violet-500", accent: "hover:border-violet-300" },
          { show: (msg?.awaiting_confirmation || 0) > 0, value: msg?.awaiting_confirmation, label: "Msgs Awaiting Confirmation", to: "/messages?tab=history", icon: MessageSquare, tone: "text-amber-600", dot: "bg-amber-500", accent: "hover:border-amber-300" },
          { show: (msg?.follow_up || 0) > 0, value: msg?.follow_up, label: "Message Follow-ups", to: "/messages?tab=history", icon: MessageSquare, tone: "text-blue-600", dot: "bg-blue-500", accent: "hover:border-blue-300" },
          { show: (msg?.failed || 0) > 0, value: msg?.failed, label: "Failed Deliveries", to: "/messages?tab=history", icon: MessageSquare, tone: "text-red-600", dot: "bg-red-500", accent: "hover:border-red-300" },
        ].filter((x) => x.show);
        return (
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="needs-attention-strip">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4 text-primary" />
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Needs Attention</p>
            </div>
            {items.length === 0 ? (
              <p className="text-sm text-emerald-600 font-medium flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" /> All clear — nothing needs your attention right now.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {items.map((it) => (
                  <button key={it.label} onClick={() => navigate(it.to)} data-testid={`attention-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`group flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-2.5 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm ${it.accent}`}>
                    <it.icon className={`w-4.5 h-4.5 ${it.tone}`} strokeWidth={1.75} />
                    <div>
                      <p className={`font-heading text-lg font-bold leading-none ${it.tone}`}>{it.value}</p>
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mt-1">{it.label}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Primary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi i={0} icon={Building2} label="Properties" value={s.total_properties} sub={`${s.total_units} total units`} accent="bg-blue-50 text-blue-600 dark:bg-blue-500/10" to="/properties" />
        <Kpi i={1} icon={Home} label="Occupancy" value={`${s.occupancy_rate}%`} sub={`${s.occupied_units} occupied · ${s.vacant_units} vacant`} accent="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10" to="/leases" />
        <Kpi i={2} icon={PiggyBank} label="Total Equity" value={money(s.total_equity)} sub="Across portfolio" accent="bg-violet-50 text-violet-600 dark:bg-violet-500/10" to="/reports" />
        <Kpi i={3} icon={Wallet} label="Net Cash Flow" value={money(s.net_cash_flow)} tone={s.net_cash_flow >= 0 ? "text-emerald-600" : "text-red-600"} sub="This month" accent="bg-amber-50 text-amber-600 dark:bg-amber-500/10" to="/reports" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi i={4} icon={TrendingUp} label="Rent Expected" value={money(s.rent_expected)} accent="bg-blue-50 text-blue-600 dark:bg-blue-500/10" to="/payments" />
        <Kpi i={5} icon={Activity} label="Rent Collected" value={money(s.rent_collected)} tone="text-emerald-600" accent="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10" to="/payments" />
        <Kpi i={6} icon={AlertTriangle} label="Outstanding Rent" value={money(s.delinquent_rent)} tone="text-red-600" accent="bg-red-50 text-red-600 dark:bg-red-500/10" to={L.overdueRent} />
        <Kpi i={7} icon={Layers} label="Monthly Expenses" value={money(s.monthly_expenses)} accent="bg-slate-100 text-slate-600 dark:bg-slate-500/10" to="/expenses" />
      </div>

      {/* Mini-stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi i={8} icon={Wrench} label="Open Maintenance" value={s.open_maintenance} accent="bg-amber-50 text-amber-600 dark:bg-amber-500/10" to={L.openMaint} />
        <Kpi i={9} icon={FileWarning} label="Leases Expiring (90d)" value={s.upcoming_lease_expirations} accent="bg-amber-50 text-amber-600 dark:bg-amber-500/10" to={L.leaseExp} />
        <Kpi i={10} icon={ShieldAlert} label="Insurance Renewals" value={s.upcoming_insurance_renewals} accent="bg-blue-50 text-blue-600 dark:bg-blue-500/10" to={L.insurance} />
        <Kpi i={11} icon={CalendarClock} label="Upcoming Payments" value={s.upcoming_payments} accent="bg-violet-50 text-violet-600 dark:bg-violet-500/10" to="/calendar" />
      </div>

      {/* Zelle Payments & Rent Reminders */}
      {z && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className={card} data-testid="dashboard-zelle">
            <div className="flex items-center gap-2 mb-4">
              <Banknote className="w-5 h-5 text-primary" />
              <h3 className="font-heading font-semibold">Zelle Payments</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Received Today" value={z.zelle.received_today} to="/zelle" />
              <MiniStat label="Posted Automatically" value={z.zelle.posted_automatically} tone="text-emerald-600" to="/zelle" />
              <MiniStat label="Needs Review" value={z.zelle.needs_review} tone={z.zelle.needs_review ? "text-amber-600" : ""} to="/zelle" />
              <MiniStat label="Duplicates Blocked" value={z.zelle.duplicates_blocked} to="/zelle" />
              <MiniStat label="Total Zelle (Month)" value={money(z.zelle.total_zelle_month)} tone="text-primary" wide to="/zelle" />
            </div>
          </div>
          <div className={card} data-testid="dashboard-reminders">
            <div className="flex items-center gap-2 mb-4">
              <BellRing className="w-5 h-5 text-primary" />
              <h3 className="font-heading font-semibold">Rent Reminders</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Unpaid This Month" value={z.reminders.unpaid_tenants} tone={z.reminders.unpaid_tenants ? "text-red-600" : ""} to={L.overdueRent} />
              <MiniStat label="Partial Payments" value={z.reminders.partial_payments} tone={z.reminders.partial_payments ? "text-amber-600" : ""} to={L.partial} />
              <MiniStat label="Total Unpaid Balance" value={money(z.reminders.total_unpaid_balance)} tone="text-red-600" wide to={L.overdueRent} />
              <MiniStat label="Reminders Sent" value={z.reminders.reminders_sent} to="/reminders" />
              <MiniStat label="Failed Reminders" value={z.reminders.failed_reminders} tone={z.reminders.failed_reminders ? "text-red-600" : ""} to="/reminders" />
            </div>
          </div>
        </div>
      )}

      {/* Action Center + Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`${card} lg:col-span-1`} data-testid="action-center">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold">Action Center</h3>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {s.action_center.length === 0 && <p className="text-sm text-muted-foreground">All clear — no urgent items.</p>}
            {s.action_center.map((a, i) => {
              const link = actionLink(a.type);
              return (
              <div key={`${a.type}-${a.label}-${a.date}-${i}`}
                onClick={link ? () => navigate(link) : undefined}
                role={link ? "button" : undefined}
                data-testid={`action-item-${i}`}
                className={`flex items-start gap-3 p-2.5 rounded-lg transition-colors ${link ? "cursor-pointer hover:bg-muted/60" : "hover:bg-muted/40"}`}>
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${sevDot[a.severity]}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-semibold ${sevText[a.severity]}`}>{a.type}</p>
                  <p className="text-sm truncate">{a.label}</p>
                  {a.doc_id && <span onClick={(e) => e.stopPropagation()}><DocLinks docId={a.doc_id} label={a.doc_label} /></span>}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{a.date?.slice(5)}</span>
              </div>
              );
            })}
          </div>
        </div>

        <div className={`${card} lg:col-span-2`}>
          <h3 className="font-heading font-semibold mb-4">Income vs Expenses</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={s.income_trend}>
              <defs>
                <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563eb" stopOpacity={0.3} /><stop offset="100%" stopColor="#2563eb" stopOpacity={0} /></linearGradient>
                <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} /><stop offset="100%" stopColor="#ef4444" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="income" stroke="#2563eb" strokeWidth={2.5} fill="url(#gInc)" />
              <Area type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2.5} fill="url(#gExp)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={card}>
          <h3 className="font-heading font-semibold mb-4">Expenses by Category</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={s.expenses_by_category} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {s.expenses_by_category.map((entry, i) => <Cell key={entry.name} fill={CHART[i % CHART.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} formatter={(v) => money(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className={`${card} lg:col-span-2`}>
          <h3 className="font-heading font-semibold mb-4">Property Performance (Annual Income vs NOI)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={s.property_performance} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} formatter={(v) => money(v)} />
              <Bar dataKey="income" fill="#2563eb" radius={[6, 6, 0, 0]} />
              <Bar dataKey="noi" fill="#10b981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
